import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { GfFlyerItemCategory } from "@swagger/typescript-fetch-goflyer";
import {
  FrontPage,
  NoCategory,
} from "../../../../utils/constant/stringConstant";
import { adminApiService } from "../../../../utils/api";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";

interface ExportCategoriesModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (selectedCategories: string[]) => void;
  categories: GfFlyerItemCategory[];
  flyerItemListId?: string;
}

export const ExportCategoriesModal: React.FC<ExportCategoriesModalProps> = ({
  open,
  onClose,
  onExport,
  categories,
  flyerItemListId,
}) => {
  const { t } = useTranslation();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(true);
  const [displayCategories, setDisplayCategories] = useState<
    GfFlyerItemCategory[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadDisplayCategories = async () => {
      setCategoriesLoading(true);

      let noCategoryEntry: GfFlyerItemCategory | null = null;
      if (flyerItemListId) {
        try {
          const response =
            await adminApiService.gfFlyerItemListAdminControllerGetCategoryCount(
              flyerItemListId,
            );
          const fromApi = response.categoryCounts?.find(
            (category) => category.id === NoCategory,
          );
          if (fromApi) {
            noCategoryEntry = {
              id: NoCategory,
              name: fromApi.name,
            } as GfFlyerItemCategory;
          }
        } catch (error) {
          console.error("Error fetching category counts for export:", error);
        }
      }

      const updatedCategories = [...categories];
      const frontPageCategory = updatedCategories.find(
        (cat) => cat.name === FrontPage,
      );

      if (!frontPageCategory && updatedCategories.length > 0) {
        updatedCategories.push({
          id: FrontPage,
          name: FrontPage,
        } as GfFlyerItemCategory);
      }

      if (
        noCategoryEntry &&
        !updatedCategories.some((cat) => cat.id === NoCategory)
      ) {
        updatedCategories.push(noCategoryEntry);
      }

      setDisplayCategories(updatedCategories);

      if (updatedCategories.length > 0) {
        setSelectedCategories(
          updatedCategories.map((cat) => cat.id as string),
        );
        setSelectAll(true);
      } else {
        setSelectedCategories([]);
        setSelectAll(false);
      }

      setCategoriesLoading(false);
    };

    loadDisplayCategories();
  }, [categories, open, flyerItemListId]);

  const getCategoryDisplayName = (category: GfFlyerItemCategory) => {
    if (category.id === NoCategory) {
      return t(messages.No_Category());
    }
    return category.name;
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        const newSelected = prev.filter((id) => id !== categoryId);
        setSelectAll(false);
        return newSelected;
      } else {
        const newSelected = [...prev, categoryId];
        setSelectAll(newSelected.length === displayCategories.length);
        return newSelected;
      }
    });
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedCategories([]);
      setSelectAll(false);
    } else {
      setSelectedCategories(
        displayCategories.map((category) => category.id as string)
      );
      setSelectAll(true);
    }
  };

  const handleExport = () => {
    setLoading(true);
    onExport(selectedCategories);
    setTimeout(() => setLoading(false), 1000);
  };

  const handleCloseModal = () => {
    setSelectedCategories([]);
    setSelectAll(false);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
      data-testid="excel-export-categories-modal"
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: "#f9f9f9",
        }}
        data-testid="excel-export-categories-modal-title"
      >
        <FileDownloadOutlinedIcon sx={{ mr: 1, color: "#ff8038" }} />
        <Typography variant="h6" component="div" fontWeight="bold">
          {t(messages.Export_Categories())}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            {t(messages.Select_Categories_To_Export())}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(messages.Export_Categories_Description())}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: "#ff8038" }} />
          <Typography variant="subtitle1" fontWeight="medium">
            {t(messages.Available_Categories())}
          </Typography>
          <Box flexGrow={1} />
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAllChange}
                data-testid="excel-export-select-all-checkbox"
                sx={{
                  color: "#ff8038",
                  "&.Mui-checked": {
                    color: "#ff8038",
                  },
                }}
              />
            }
            label={
              <Typography fontWeight="medium">
                {t(messages.Select_All())}
              </Typography>
            }
          />
        </Box>

        {categoriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} sx={{ color: "#ff8038" }} />
          </Box>
        ) : displayCategories.length === 0 ? (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            data-testid="excel-export-no-categories-alert"
          >
            {t(messages.No_Categories_Available())}
          </Alert>
        ) : (
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, maxHeight: "250px", overflow: "auto" }}
          >
            <Grid
              container
              spacing={1}
              data-testid="excel-export-categories-container"
            >
              {displayCategories.map((category) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={category.id}
                  data-testid={`excel-export-category-item-category`}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      display: "flex",
                      border: "1px solid",
                      borderColor: selectedCategories.includes(
                        category.id as string
                      )
                        ? "#ff8038"
                        : "rgba(0,0,0,0.12)",
                      borderRadius: 1,
                      bgcolor: selectedCategories.includes(
                        category.id as string
                      )
                        ? "rgba(255, 128, 56, 0.05)"
                        : "transparent",
                    }}
                    data-testid={`excel-export-category-item-${category.id}`}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedCategories.includes(
                            category.id as string
                          )}
                          onChange={() =>
                            handleCategoryChange(category.id as string)
                          }
                          data-testid={`excel-export-category-checkbox-${category.id}`}
                          sx={{
                            color: "#ff8038",
                            "&.Mui-checked": {
                              color: "#ff8038",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          noWrap
                          title={getCategoryDisplayName(category)}
                        >
                          {getCategoryDisplayName(category)}
                        </Typography>
                      }
                      sx={{ flexGrow: 1, mr: 0 }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {selectedCategories.length > 0 && (
          <Box mt={2} display="flex" alignItems="center">
            <Typography variant="body2" color="text.secondary" mr={1}>
              {t(messages.Selected_Categories())}:
            </Typography>
            <Chip
              label={selectedCategories.length}
              size="small"
              color="primary"
              sx={{
                bgcolor: "#ff8038",
                height: "22px",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>
        )}

        {selectedCategories.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t(messages.Selected_Categories())}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Button
          onClick={handleCloseModal}
          color="inherit"
          variant="outlined"
          sx={{ borderColor: "#d0d0d0" }}
          disabled={loading}
          data-testid="excel-export-cancel-button"
        >
          {t(messages.Cancel())}
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          sx={{
            backgroundColor: "#ff8038",
            "&:hover": {
              backgroundColor: "#e06b2d",
            },
            minWidth: "120px",
          }}
          disabled={
            selectedCategories.length === 0 || loading || categoriesLoading
          }
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FileDownloadOutlinedIcon />
            )
          }
          data-testid="excel-export-submit-button"
        >
          {loading ? t(messages.Exporting()) : t(messages.Export())}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
